import * as assert from 'assert';
import { computed } from 'mobx';
import { IUserFact } from '../Models/IUser';
import { SignupService } from '../Services/SignupService';
import { IFamilyFromServer } from '../Services/UserFamilyService';
import { ApplicationStore } from '../_stores/ApplicationStore';

export interface IInviteInformation {
    firstName: string;
    email: string;
}

export class OnboardingStore {
    private _email?: string;
    private _firstName?: string;
    private _lastName?: string;
    private _password?: string;
    private _storyName?: string;
    private _familyToJoinID?: number;
    private _familyName?: string;
    private _familyInvites?: Array<IInviteInformation>;
    private _signupService: SignupService = new SignupService('');
    private store: ApplicationStore;
    private _createdStoryID?: number;
    private _invitedUserInformation?: { inviteKey: string; email: string; password: string };

    public constructor(store: ApplicationStore) {
        this.store = store;
    }

    @computed
    public get createdStoryID() {
        return this._createdStoryID;
    }

    @computed
    public get storyTitle() {
        return this._storyName;
    }

    @computed
    public get familyName() {
        return this._familyName;
    }

    @computed
    public get invites() {
        return this._familyInvites;
    }

    public get wasInvited() {
        return this._invitedUserInformation;
    }

    @computed
    public get invitedUserInformation(): { inviteKey: string; email: string; password: string } | undefined {
        if (this._invitedUserInformation && this._password) {
            this._invitedUserInformation.password = this._password;
        }
        return this._invitedUserInformation;
    }

    public setInvitedUserInformation(inviteKey: string, email: string): void {
        this._invitedUserInformation = {
            password: '',
            inviteKey,
            email,
        };
    }

    public setAccountInformation(firstName: string, lastName: string, email: string, password: string): void {
        assert(firstName !== '' && firstName, 'firstName was passed in empty or undefined');
        assert(lastName !== '' && lastName, 'lastName was passed in empty or undefined');
        assert(email !== '' && email, 'email was passed in empty or undefined');
        assert(password !== '' && password, 'password was passed in empty or undefined');

        this._email = email;
        this._firstName = firstName;
        this._lastName = lastName;
        this._password = password;
    }

    public get userInformation() {
        return {
            email: this._email,
            firstName: this._firstName,
            lastName: this._lastName,
        };
    }

    public setProfileInformation(
        firstName: string,
        lastName: string,
        email: string,
        userFacts: Array<IUserFact>,
        profileImage: any,
    ): void {
        this._firstName = firstName;
        this._lastName = lastName;
        this._email = email;
    }

    public setStoryInformation(storyName: string, isPrivate: boolean): void {
        this._storyName = storyName;
    }

    public setFamilyInformation(familyName: string, invites: Array<IInviteInformation>): void {
        this._familyName = familyName;
        this._familyInvites = invites;
    }

    public async createStoryPrivately() {
        if (!this._storyName) {
            return;
        }
        const story = await this.store.storyStore.createStory(this._storyName, undefined);
        this._createdStoryID = story.id;
    }

    public async createFamilyAndStory() {
        if (!this._storyName) {
            return;
        }
        const family = (await this.createOrInviteUserToFamily(
            this.store.authorizationStore.user.id,
        )) as IFamilyFromServer;
        const story = await this.store.storyStore.createStory(this._storyName, family.id);
        this._createdStoryID = story.id;
    }

    public async createStory() {
        if (!this._storyName) {
            return;
        }
        const story = await this.store.storyStore.createStory(this._storyName);
        this._createdStoryID = story.id;
    }

    public async postAllOnboardingData(): Promise<void> {
        const signupData = {
            first_name: this._firstName,
            last_name: this._lastName,
            email: this._email,
            password: this._password,
        };

        const authCredentials = await this._signupService.signup(signupData);
        await this.store.authorizationStore.loginUserWithToken(authCredentials);
    }

    private async createOrInviteUserToFamily(userID: number): Promise<IFamilyFromServer | void> {
        if (!this._familyName || !this._familyInvites) {
            return;
        }
        if (this._familyToJoinID) {
            console.assert(false, 'This should not happen, user should have created an account before this page');
        } else {
            return this.store.userFamilyStore.createFamily(this._familyName, this._familyInvites);
        }
    }

    public async addAllPhotosToStory() {
        if (!this._createdStoryID) {
            return;
        }
        const allPhotoIDs = this.store.userPhotoStore.userPhotos.map(photo => photo.id);
        if (allPhotoIDs.length === 0) {
            return;
        }

        return this.store.storyStore.addPhotosToStory(this._createdStoryID, allPhotoIDs);
    }
}



// WEBPACK FOOTER //
// ./src/Stores/OnboardingStore.ts